/* Secure admin-only preview that renders the public shape of a listing
   regardless of its moderation status. The backend endpoint
   /api/admin/broker-listings/{kind}/{lid}/preview enforces require_admin()
   so a non-admin can't reach it by guessing the URL. */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { ListingPreview } from "../components/koop/ListingPreview";
import { Button } from "../components/ui/button";

const AdminListingPreview = () => {
  const { kind, lid } = useParams();
  useSeo({ title: "Admin voorbeeld · WoningAlert" });
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null); setErr("");
    api(`/admin/broker-listings/${kind}/${lid}/preview`)
      .then(setData)
      .catch((e) => { setErr(e.message); toast.error(e.message); });
  }, [kind, lid]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-5xl px-4 py-4 sm:px-6">
        <div className="flex items-center justify-between">
          <Link to={`/admin/broker-listings/${kind}/${lid}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy">
            <ArrowLeft size={14} /> Terug naar bewerken
          </Link>
          <Button asChild variant="outline" className="rounded-xl">
            <Link to="/admin/broker-listings">Alle listings</Link>
          </Button>
        </div>
      </div>
      <div className="mx-auto max-w-5xl px-4 pb-10 sm:px-6">
        <div className="mb-4 flex items-center gap-2 rounded-2xl bg-navy p-3 text-sm text-white">
          <EyeOff size={16} />
          <span>
            <strong>Admin voorbeeld.</strong> Deze listing is {data?.moderation_status === "approved" ? "live" : "niet publiek zichtbaar"}.
            Zo zien bezoekers de advertentie zodra deze is goedgekeurd.
          </span>
        </div>

        {err ? (
          <div className="rounded-3xl border border-danger/30 bg-white p-10 text-center text-danger">{err}</div>
        ) : !data ? (
          <div className="flex items-center justify-center p-16"><Loader2 className="animate-spin text-brandblue" size={28} /></div>
        ) : kind === "koop" ? (
          <div className="rounded-3xl border border-border bg-white p-4 shadow-sm sm:p-6">
            <ListingPreview data={data} showContact />
          </div>
        ) : (
          <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
            {/* Simple rendering for rent listings; no public rental detail page component yet. */}
            {(data.image || data.gallery?.[0]) && <img src={data.image || data.gallery[0]} alt="" className="h-72 w-full rounded-2xl object-cover" />}
            <h1 className="mt-6 font-display text-3xl font-extrabold text-navy">{data.title}</h1>
            <p className="mt-1 text-slate-500">{data.street}, {data.city}</p>
            <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-700">
              <span>€{Number(data.price || 0).toLocaleString("nl-NL")}/mnd</span>
              <span>· {data.bedrooms || 0} slaapkamers</span>
              <span>· {data.m2 || 0} m²</span>
              <span>· Energielabel {data.energy_label || "—"}</span>
            </div>
            {data.description && <p className="mt-6 whitespace-pre-line text-slate-600">{data.description}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminListingPreview;
